import {DocumentPart, PartType} from '@dv5/domain'
import {messagesService} from "@dv5/api"
import {
    AddDocumentPartMessage,
    ChangeDocumentDescriptionMessage,
    CreatePartFromTemplateMessage,
    GetDocumentDescriptionMessage,
    PrependDocumentPartMessage,
    PrependPartFromTemplateMessage
} from '../types'

const PART_INDEX_STEP = 100

class DocumentApi {
    getNextPartIndex(parts: Array<DocumentPart>): number {
        if (!parts || parts.length === 0) {
            return PART_INDEX_STEP
        }

        const maxEntityIndex = parts.reduce((max, item) => {
            return item.partIndex > max ? item.partIndex : max
        }, -Infinity)

        return Math.max(PART_INDEX_STEP, maxEntityIndex + PART_INDEX_STEP)
    }

    buildAddPayload(
        documentId: string,
        parts: DocumentPart[],
        layers: string[],
        tags: string[],
        partType: PartType,
        content: string | null
    ): AddDocumentPartMessage {
        return {
            documentId,
            partIndex: this.getNextPartIndex(parts),
            partType,
            layers: layers.join(','),
            tags: tags.join(','),
            content,
        }
    }

    addPart(message: AddDocumentPartMessage): void {
        messagesService.send<AddDocumentPartMessage>('documents.add-part', message)
    }

    async importPartFromFile(
        file: File,
        documentId: string,
        parts: DocumentPart[],
        layers: string[],
        tags: string[],
        partType: PartType.MARKDOWN | PartType.PLAINTEXT,
    ): Promise<void> {
        const extension = partType === PartType.MARKDOWN ? '.md' : '.txt'
        if (!file.name.toLowerCase().endsWith(extension)) {
            throw new Error(`Please select a ${extension} file.`)
        }
        let content: string
        try {
            content = await file.text()
        } catch {
            throw new Error(`Could not read "${file.name}". Please try again.`)
        }
        this.addPart(this.buildAddPayload(documentId, parts, layers, tags, partType, content))
    }

    prependPart(message: PrependDocumentPartMessage): void {
        messagesService.send<PrependDocumentPartMessage>('documents.prepend-part', message)
    }

    addPartFromTemplate(message: CreatePartFromTemplateMessage): void {
        messagesService.send<CreatePartFromTemplateMessage>('documents.create-part-from-template', message)
    }

    prependPartFromTemplate(message: PrependPartFromTemplateMessage): void {
        messagesService.send<PrependPartFromTemplateMessage>('documents.prepend-part-from-template', message)
    }

    getParts(documentId: string, layers: string[], tags: string[]): void {
        messagesService.send('documents.get-parts', { documentId, layers, tags })
    }

    getAllParts(documentId: string): void {
        messagesService.send('documents.get-all-parts', { documentId })
    }

    getDescription(documentId: string): void {
        const message: GetDocumentDescriptionMessage = { documentId }
        messagesService.send<GetDocumentDescriptionMessage>('documents.get-description', message)
    }

    changeDescription(documentId: string, content: string): void {
        const message: ChangeDocumentDescriptionMessage = { documentId, content }
        messagesService.send<ChangeDocumentDescriptionMessage>('documents.change-description', message)
    }

    changePartContent(documentId: string, partId: string, content: string): void {
        messagesService.send('documents.change-part-content', { documentId, partId, content })
    }

    changePartHeaderType(documentId: string, partId: string, header: string, type: string): void {
        messagesService.send('documents.change-part-header-type', { documentId, partId, header, type })
    }

    changePartLayers(documentId: string, partId: string, layers: string): void {
        messagesService.send('documents.change-part-layers', { documentId, partId, layers })
    }

    assignPartToTags(documentId: string, partId: string, tags: string): void {
        messagesService.send('documents.assign-part-to-tags', { documentId, partId, tags })
    }

    removePart(documentId: string, partId: string): void {
        messagesService.send('documents.remove-part', { documentId, partId })
    }

    changePartPositions(documentId: string, positions: Array<{ id: string; index: number }>): void {
        messagesService.send('documents.change-part-positions', { documentId, positions })
    }

    clonePart(documentId: string, partId: string, partIndex: number, headerPrefix: string): void {
        messagesService.send('documents.clone-part', { documentId, partId, partIndex, headerPrefix })
    }

    getPartComments(documentId: string, partId: string): void {
        messagesService.send('documents.get-part-comments', { documentId, partId })
    }

    addComment(documentId: string, sourceItemId: string, content: string): void {
        messagesService.send('documents.add-comment', { documentId, sourceItemId, content })
    }

    editComment(documentId: string, commentId: string, content: string): void {
        messagesService.send('documents.edit-part-comment', { documentId, commentId, content })
    }

    removeComment(documentId: string, sourceItemId: string, commentId: string): void {
        messagesService.send('documents.remove-part-comment', { documentId, sourceItemId, commentId })
    }

    exportToCsv(part: DocumentPart): void {
        const blob = new Blob([part.partContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `${part.partHeader || 'document-part'}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }
}

export const documentApi = new DocumentApi()
